import { useMemo } from 'react';
import { ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import type { SalesComparison } from './salesComparisonApi';
import type { ReportRow } from './salesComparisonTypes';
import { ComparisonTableHeader } from './ComparisonTableHeader';
import { ComparisonBodyRow } from './ComparisonBodyRow';

interface Props {
    data: SalesComparison;
}

// Builds the TanStack column model (a label column + one per store/total) and the flattened rows, then
// renders the table from its header groups + row model. The pivot tiers and section rows are handled by
// ComparisonTableHeader / ComparisonBodyRow.
export function SalesComparisonTable({ data }: Props) {
    const columns = useMemo<ColumnDef<ReportRow>[]>(() => [
        { id: 'label', header: 'Mätetal', cell: ({ row }) => (row.original.kind === 'data' ? row.original.label : null) },
        ...data.columns.map((c, i): ColumnDef<ReportRow> => ({
            id: `c${i}`,
            header: c.header,
            cell: ({ row }) => {
                if (row.original.kind !== 'data') return null;
                const cell = row.original.cells[i];
                return cell ? <>{cell.display}{cell.star ? ' ⭐' : ''}</> : null;
            },
        })),
    ], [data.columns]);

    const rows = useMemo<ReportRow[]>(() => {
        const out: ReportRow[] = [];
        for (const section of data.sections) {
            out.push({ kind: 'section', title: section.title });
            for (const r of section.rows) out.push({ kind: 'data', style: r.style, label: r.label, cells: r.cells });
        }
        return out;
    }, [data.sections]);

    const table = useReactTable({ data: rows, columns, getCoreRowModel: getCoreRowModel() });

    return (
        <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 6 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <ComparisonTableHeader columns={data.columns} headerGroups={table.getHeaderGroups()} />
                <tbody>
                    {table.getRowModel().rows.map(row => (
                        <ComparisonBodyRow key={row.id} row={row} columns={data.columns} />
                    ))}
                </tbody>
            </table>
        </div>
    );
}
